interface InitParams {
  dsn: string;
  release: string;
  userId: string;
  /** Sentry environment tag — e.g. 'development', 'production', 'test'. */
  environment?: string;
}

interface Buffered {
  err: unknown;
  context?: Record<string, unknown>;
}

let initialized = false;
let buffer: Buffered[] = [];
let initPromise: Promise<void> | null = null;
let SentryModule: typeof import('@sentry/browser') | null = null;

const UUID_RE = /[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}/gi;

function scrubString(s: string): string {
  return s.replace(UUID_RE, '<uuid>');
}

function scrubSensitive(event: {
  message?: string;
  breadcrumbs?: Array<{ message?: string; data?: unknown }>;
  exception?: { values?: Array<{ value?: string }> };
  request?: { url?: string };
}) {
  if (event.message) event.message = scrubString(event.message);
  if (event.breadcrumbs) {
    event.breadcrumbs = event.breadcrumbs.map((b) => ({
      ...b,
      message: b.message ? scrubString(b.message) : b.message,
    }));
  }
  if (event.exception?.values) {
    event.exception.values = event.exception.values.map((v) => ({
      ...v,
      value: v.value ? scrubString(v.value) : v.value,
    }));
  }
  if (event.request?.url) {
    event.request.url = scrubString(event.request.url);
  }
  return event;
}

export async function initSentry(params: InitParams): Promise<void> {
  if (initPromise) return initPromise;
  initPromise = (async () => {
    const Sentry = await import('@sentry/browser');
    SentryModule = Sentry;
    Sentry.init({
      dsn: params.dsn,
      release: params.release,
      environment: params.environment,
      sendDefaultPii: false,
      tracesSampleRate: 0,
      replaysSessionSampleRate: 0,
      replaysOnErrorSampleRate: 0,
      beforeSend: (event) => scrubSensitive(event as never) as never,
      beforeBreadcrumb: (b) => ({ ...b, message: b.message ? scrubString(b.message) : b.message }),
    });
    Sentry.setUser({ id: params.userId });
    initialized = true;
    const queued = buffer;
    buffer = [];
    for (const { err, context } of queued) Sentry.captureException(err, { extra: context });
  })();
  return initPromise;
}

export function captureException(err: unknown, context?: Record<string, unknown>): void {
  if (!initialized || !SentryModule) {
    buffer.push({ err, context });
    return;
  }
  SentryModule.captureException(err, { extra: context });
}

/** @internal — only for tests. */
export function resetForTest(): void {
  initialized = false;
  SentryModule = null;
  buffer = [];
  initPromise = null;
}
